import { parse, stringify } from 'lossless-json'
import { stringifyEntityRef, SystemEntity } from '@backstage/catalog-model';
import { discoveryApiRef, fetchApiRef, microsoftAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { Button, Card, CardContent, Checkbox, Chip, Container, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, FormControl, IconButton, InputLabel, List, ListItem, ListItemIcon, ListItemText, makeStyles, MenuItem, Select, Snackbar, Theme, Typography } from '@material-ui/core';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import InfoIcon from '@material-ui/icons/Info';
import FilterListIcon from '@material-ui/icons/FilterList';
import LocalLaundryServiceIcon from '@material-ui/icons/LocalLaundryService';
import UpdateIcon from '@material-ui/icons/Update';
import SendIcon from '@material-ui/icons/Send';
import InboxIcon from '@material-ui/icons/Inbox';
import Alert from '@material-ui/lab/Alert';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../../../components/accordion/accordion.styled';
import SimpleConfirmDialog from '../../../../../../components/dialog';
import { DeadMessage, FetchMqStatsService, MovedMessageInfo } from '../../../../../../services/FetchMqStatsService';
import { OverseerService } from '../../../../../../services/OverseerService';

import { Subscription } from '@backstage/types'
import { usePermission } from '@backstage/plugin-permission-react';
import { parkingLotManagerDeletePermission } from '@internal/backstage-plugin-supply-features-common';
import { AppEnvironment } from '@internal/open-catalog';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { WorkspacesService } from '../../../../../../services/WorkspacesService';

const useStylesMessageDetails = makeStyles((theme: Theme) =>
  createStyles({
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 1),
      ...theme.mixins.toolbar,
      justifyContent: 'flex-start',
    },
    item: { width: '50%' },
    list: { width: '100%' },
    listStriped: {
      '& li:nth-child(odd)': {
        backgroundColor: '#f2f2f2'
      }
    },
    itemKey: { width: '30%' },
    itemValue: {
      width: '70%',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
  })
);

const extractHeader = (headers: any, key: string) => {
  if (headers) {
    return headers[key];
  }
  return undefined;
}

const MessageDetails = (props: {
  message: DeadMessage,
  onCloseClicked: () => void
}) => {

  const { message, onCloseClicked } = props;
  const { properties, payload } = message;
  const { headers } = properties;

  const classes = useStylesMessageDetails();
  const [expanded, setExpanded] = useState<string>('panel0');

  const transactionId = extractHeader(headers, 'transactionid') || extractHeader(headers, 'transactionId') || extractHeader(headers, 'transaction_id');
  const correlationId = extractHeader(headers, 'correlationid') || extractHeader(headers, 'correlationId') || extractHeader(headers, 'correlation_id');
  const parsedPayload = stringify(parse(payload || ''), undefined, 2);

  const handleChange = (panel: any) => (_event: ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (<div role="presentation">
    {message && (<>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onCloseClicked}>
          <ChevronRightIcon />
        </IconButton>
      </div>
      <>
        <Accordion expanded={expanded === 'panel0'} onChange={handleChange('panel0')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Summary</AccordionSummary>
          <AccordionDetails>
            <List dense className={classes.list} >
              <ListItem>
                <ListItemText className={classes.item} primary={headers.type} secondary={headers.source || 'Unknown source'} />
                <ListItemText className={classes.item} primary={headers.workspace_alias || 'Workspace'} secondary={headers.workspace || 'Unknown'} />
              </ListItem>
              <ListItem>
                <ListItemText className={classes.item} primary="ID" secondary={properties.message_id || 'Unknown'} />
                <ListItemText className={classes.item} primary="Time" secondary={new Date(properties.timestamp * 1000).toISOString()} />
              </ListItem>
              <ListItem>
                <ListItemText className={classes.item} primary="Transaction" secondary={transactionId || 'Unknown'} />
                <ListItemText className={classes.item} primary="Correlation" secondary={correlationId || 'Unknown'} />
              </ListItem>
              <ListItem>
                <ListItemText className={classes.item} primary="Error" secondary={
                  <Container component="textarea"
                             disabled
                             rows={5}
                             style={{ border: "none", cursor: "text" }}
                             value={headers.exception_cause}>
                    &nbsp;
                  </Container>
                } />
              </ListItem>
            </List>

          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expanded === 'panel1'} onChange={handleChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Properties</AccordionSummary>
          <AccordionDetails>
            <List dense className={`${classes.list} ${classes.listStriped}`} >
              {[...Object.entries(properties)]
                .filter(fproperty => !['headers'].includes(fproperty[0]))
                .map((property, pindex) => (
                  <ListItem key={pindex}>
                    <ListItemText className={classes.itemKey} primary={property[0]} />
                    <ListItemText className={classes.itemValue} secondary={`${property[1]}`} />
                  </ListItem>
                ))}
            </List>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expanded === 'panel2'} onChange={handleChange('panel2')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Headers</AccordionSummary>
          <AccordionDetails>
            <List dense className={`${classes.list} ${classes.listStriped}`} >
              {[...Object.entries(headers)]
                .filter(fheader => !['exception', 'exception_cause'].includes(fheader[0]) && !fheader[0].startsWith('x-'))
                .map((header, hindex) => (
                  <ListItem key={hindex}>
                    <ListItemText className={classes.itemKey} primary={header[0]} />
                    <ListItemText className={classes.itemValue} secondary={`${header[1]}`} />
                  </ListItem>
                ))}
            </List>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expanded === 'panel3'} onChange={handleChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Content</AccordionSummary>
          <AccordionDetails>
            <Container component="textarea"
                       disabled
                       rows={25}
                       style={{ border: "none", cursor: "text" }}
                       value={parsedPayload}>
              &nbsp;
            </Container>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={expanded === 'panel4'} onChange={handleChange('panel4')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>X-Death</AccordionSummary>
          <AccordionDetails>
            <List dense className={`${classes.list} ${classes.listStriped}`} >
              {[...Object.entries(headers)]
                .filter(fx => !['x-death'].includes(fx[0]) && fx[0].startsWith('x-'))
                .map((xheader, xhindex) => (
                  <ListItem key={xhindex}>
                    <ListItemText className={classes.itemKey} primary={xheader[0]} />
                    <ListItemText className={classes.itemValue} secondary={`${xheader[1]}`} />
                  </ListItem>
                ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </>
    </>)}
  </div>);
};

const useStylesMessageListItem = makeStyles((theme: Theme) =>
  createStyles({
    item: {
      alignItems: 'flex-start',
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      '&:hover': {
        backgroundColor: theme.palette.background.default,
      },
      '& > div': {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },
    },
    servicesColumn: {
      flex: '1 1 25%',
    },
    serviceRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    consumerText: {
      fontWeight: 'bold',
    },
    publisherRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginTop: '4px',
    },
    typeColumn: {
      flex: '1 1 20%',
      wordBreak: 'break-word',
    },
    errorText: {
      display: '-webkit-box',
      '-webkit-line-clamp': 3,
      '-webkit-box-orient': 'vertical',
      overflow: 'hidden',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
    },
    errorColumn: {
      flex: '1 1 40%',
      wordBreak: 'break-word',
    },
    tagContainer: {
      textAlign: 'end',
      alignSelf: 'center',
      paddingRight: theme.spacing(1),
    },
    tag: {
      margin: 0,
      marginLeft: '1em',
      letterSpacing: 0.75,
    },
  }),
);

const MessageListItem = (props: {
  message: DeadMessage;
  onSelectionChanged: (isChecked: boolean, selected: DeadMessage) => void;
  onClipboardClicked: () => void;
  onDetailsClicked: () => void;
}) => {
  const { message, onSelectionChanged, onClipboardClicked, onDetailsClicked } = props;
  const { headers } = message.properties;

  const classes = useStylesMessageListItem();
  const [isChecked, setIsChecked] = useState(false);

  const toggleCheckbox = () => {
    setIsChecked(!isChecked);
    onSelectionChanged(!isChecked, message);
  };

  const handleClipboardClicked = () => {
    navigator.clipboard.writeText(
      `{ "properties": ${JSON.stringify(message.properties)} , "payload": ${message.payload} }`
    );
    onClipboardClicked();
  };

  const dynamicHeaders = headers as any;
  const dynamicProperties = message.properties as any;

  const filaConsumidor =
    dynamicHeaders['x-first-death-queue'] ||
    dynamicProperties.routing_key ||
    dynamicHeaders.queue ||
    'Fila Desconhecida';

  const servicoPublicador = headers.source || 'Origem Desconhecida';

  return (
    <ListItem className={classes.item} divider alignItems="flex-start">
      <ListItemIcon onClick={toggleCheckbox}>
        <Checkbox edge="start" disableRipple checked={isChecked} />
      </ListItemIcon>

      <ListItemText
        className={classes.typeColumn}
        primary={headers.type}
        secondary={`Workspace: ${headers.workspace_alias || headers.workspace || 'Unknown'}`}
        primaryTypographyProps={{ variant: 'subtitle2', style: { whiteSpace: 'normal' } }}
      />

      <ListItemText
        className={classes.servicesColumn}
        primary={
          <span className={`${classes.serviceRow} ${classes.consumerText}`} title="Serviço Consumidor (Onde ocorreu o erro)">
            <InboxIcon fontSize="small" color="error" /> {filaConsumidor}
          </span>
        }
        primaryTypographyProps={{ color: 'error', variant: 'body2' }}
        secondary={
          <span className={classes.publisherRow} title="Serviço Publicador">
            <SendIcon fontSize="small" /> {servicoPublicador}
          </span>
        }
      />

      <ListItemText
        className={classes.errorColumn}
        primary="Error"
        secondary={headers.exception_cause}
        secondaryTypographyProps={{ className: classes.errorText }}      />

      <ListItemText className={classes.tagContainer}>
        {message.properties.timestamp && (
          <Chip
            label={new Date(message.properties.timestamp * 1000).toISOString()}
            size="small"
            className={classes.tag}
          />
        )}
      </ListItemText>

      <ListItemIcon>
        <IconButton title="Copy message" onClick={handleClipboardClicked}>
          <FileCopyIcon />
        </IconButton>
      </ListItemIcon>
      <ListItemIcon>
        <IconButton title="Show details" onClick={() => onDetailsClicked()}>
          <InfoIcon />
        </IconButton>
      </ListItemIcon>
    </ListItem>
  );
};

const MessageRequeueResponseListItem = (props: { report: MovedMessageInfo }) => {

  const { report } = props;

  const classes = useStylesMessageListItem();

  return (<ListItem className={classes.item}>
    <ListItemText primary="ID" secondary={report.id} />
    <ListItemText primary="Workspace" secondary={report.workspace || 'Unknown'} />
    <ListItemText primary="Type" secondary={report.type || 'Unknown'} />
    <ListItemText primary="Returned to" secondary={report.deliveredTo} />
  </ListItem>);
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: { marginTop: '1em' },
    fullscreen: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: 'calc(100vh - 32px)',
      width: 'calc(100vw - 32px)',
      margin: 16,
      zIndex: 10000
    },
    fullscreenContent: {
      overflowX: 'auto'
    },
    loadMore: {
      padding: 0,
      marginTop: '1em'
    },
    header: {
      alignItems: 'center',
      padding: theme.spacing(2),
      paddingBottom: 0
    },
    headerItem: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    drawer: {
      width: '45vw',
      inset: 'auto !important',
      backgroundColor: theme.palette.background.default
    },
    drawerPaper: {
      width: '45vw',
    },
    queueFilterControl: {
      minWidth: 320,
      maxWidth: 600,
    },
    countChip: {
      marginLeft: theme.spacing(1),
      fontWeight: 'bold',
    },
  })
)

const proccessTenantSubdomain = async (workspacesService: WorkspacesService, systemId: string | undefined, allMessages: DeadMessage[]) => {

  const workspacesMap = allMessages.map(m => m.properties.headers)
    .reduce((u, c) => {

      if (u.has(c.workspace)) {
        if (c.workspace_alias?.length && u.get(c.workspace) !== c.workspace_alias) {
          u.set(c.workspace, c.workspace_alias);
        }
      } else {
        u.set(c.workspace, c.workspace_alias || '');
      }

      return u;

    }, new Map<string, string>());

  const wokspacesToLookupAlias = Array.from(workspacesMap.entries())
    .filter(([_, alias]) => !alias || alias.length === 0)
    .map(([workspace, _]) => workspace);

  if (systemId && wokspacesToLookupAlias.length) {
    try {
      const systemWorkspaces = await workspacesService.getAllWorkspacesBySystem('development', systemId);
      systemWorkspaces.forEach(workspace => {
        if (workspacesMap.has(workspace.racId)) {
          workspacesMap.set(workspace.racId, workspace.subdomain);
        }
      });
    } catch (error) {
      // Alias enrichment is cosmetic — never block message loading because of it.
      console.error('Error resolving workspace aliases:', error);
    }
  }

  for (const msg of allMessages) {
    if (!msg.properties.headers?.workspace_alias?.length) {
      const alias = workspacesMap.get(msg.properties.headers.workspace);
      if (alias) {
        msg.properties.headers.workspace_alias = alias;
      }
    }
  }
}

interface MoveMessagesAction {
  environment: string;
  listOfQueues: string[];
  itemsMessages: DeadMessage[];
}

export interface ParkingLotReprocessorProps {
  queues: string[];
  env?: AppEnvironment;
  system: SystemEntity;
  onRefreshByChangingQueue: () => void
}

export const ParkingLotMessageReprocessor = ({ queues, env, system, onRefreshByChangingQueue }: ParkingLotReprocessorProps) => {

  const environment = useMemo(() => env || 'production', [env]);
  const classes = useStyles();

  const storageKey = useMemo(
    () => `nidus-pl-filter-${stringifyEntityRef(system)}-${environment}`,
    [system, environment],
  );

  const [selectedQueues, setSelectedQueues] = useState<string[]>([]);

  useEffect(() => {
    if (!queues.length) {
      setSelectedQueues([]);
      return;
    }
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        // keep only queues that still exist; empty array is a valid saved state
        const valid = parsed.filter(q => queues.includes(q));
        setSelectedQueues(valid);
        return;
      }
    } catch {
      // ignore malformed storage value
    }
    setSelectedQueues(queues);
  }, [queues, storageKey]);

  const [selectedCount, setSelectedCount] = useState<number | undefined>(undefined);

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const authApi = useApi(microsoftAuthApiRef);

  const workspacesService = useMemo(
    () => new WorkspacesService(fetchApi, authApi, discoveryApi, catalogApi),
    [fetchApi, authApi, discoveryApi, catalogApi],
  );

  const [fullScreen, setFullScreen] = useState(false);
  const [showMessageDetails, setShowMessageDetails] = useState(false);
  const [messageToDetail, setMessageToDetail] = useState<DeadMessage>();

  const { value: proxyUrl } = useAsync(() => discoveryApi.getBaseUrl('proxy'), [discoveryApi]);

  const fetchSelectedCount = useCallback(async () => {
    if (!selectedQueues.length) {
      setSelectedCount(0);
      return;
    }
    if (!proxyUrl) return;
    const mqAccess = new FetchMqStatsService(proxyUrl, fetchApi, environment, environment);
    const counts = await Promise.all(selectedQueues.map(q => mqAccess.messagesCount(q)));
    setSelectedCount(counts.reduce((a, b) => a + b, 0));
  }, [proxyUrl, fetchApi, environment, selectedQueues]);

  useEffect(() => {
    fetchSelectedCount();
  }, [fetchSelectedCount]);

  const handleQueueFilterChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string[];
    const newSelection = value.includes('__all__')
      ? (selectedQueues.length === queues.length ? [] : queues)
      : value;
    setSelectedQueues(newSelection);
    try {
      localStorage.setItem(storageKey, JSON.stringify(newSelection));
    } catch {
      // ignore
    }
  };

  const [numberOfLoadedMessages, setNumberOfLoadedMessages] = useState(100);

  const [items, setItems] = useState<DeadMessage[]>([]);
  const [loading, setLoadingMessages] = useState<boolean>(true);

  const [checkedCount, setCheckedCount] = useState(0);
  const [messageToClipboardClicked, setMessageToClipboardClicked] = useState(false);

  const [showRequeueResponse, setShowRequeueResponse] = React.useState(false);
  const [movedMessages, setMovedMessages] = useState([] as MovedMessageInfo[]);

  const [handleRemoveRequestDialogOpen, setHandleRemoveRequestDialogOpen] = useState(false);

  const [onGoingTask, setOnGoingTask] = useState<Subscription | undefined>(undefined);

  const deniedRunRemoveMessagePermission = !usePermission({ permission: parkingLotManagerDeletePermission }).allowed;

  const deniedRunRemoveMessage = useMemo(() => deniedRunRemoveMessagePermission && (environment === 'production' || environment === 'staging'), [deniedRunRemoveMessagePermission, environment]);

  const fetchRequestIdRef = useRef(0);

  const fetchQueueMessages = useCallback(async (listOfQueues: string[]) => {
    setCheckedCount(0);
    const allMessagesMap: Record<string, DeadMessage[]> = {};
    const requestId = ++fetchRequestIdRef.current;

    if (!listOfQueues.length) {
      setItems([]);
      setLoadingMessages(false);
      return allMessagesMap;
    }
    if (environment && listOfQueues.length && proxyUrl) {

      setLoadingMessages(true);
      const mqAccess = new FetchMqStatsService(proxyUrl, fetchApi, environment, environment);
      const messagesMap = await Promise.all(listOfQueues.map(queue => mqAccess.readMessages(queue, numberOfLoadedMessages).then((messages) => ({ queue, messages }))));

      if (requestId !== fetchRequestIdRef.current) return allMessagesMap;

      const allMessages: DeadMessage[] = [];

      for (const { queue, messages } of messagesMap) {
        allMessages.push(...messages);
        allMessagesMap[queue] = messages;
      }

      await proccessTenantSubdomain(workspacesService, WorkspacesService.getSystemId(system), allMessages);

      if (requestId !== fetchRequestIdRef.current) return allMessagesMap;

      setItems(allMessages);
      setLoadingMessages(false);

    }
    return allMessagesMap;
  }, [proxyUrl, numberOfLoadedMessages, fetchApi, setItems, environment, workspacesService, system]);

  const allDeadMessagesMap = useMemo(() => {
    setShowRequeueResponse(showRequeueResponse);
    return fetchQueueMessages(selectedQueues);
  }, [selectedQueues, fetchQueueMessages, showRequeueResponse]);

  const fetchMoreIfNeeded = () => {
    if (items.length === numberOfLoadedMessages) {
      // triggers fetchQueueMessages
      setNumberOfLoadedMessages(numberOfLoadedMessages + 1);
    } else {
      fetchQueueMessages(selectedQueues);
    }
  };

  const onSelectionChanged = (isChecked: boolean, message: DeadMessage) => {
    if (isChecked) {
      setCheckedCount(checkedCount + 1);
    } else {
      setCheckedCount(checkedCount - 1);
    }
    message.isChecked = isChecked;
  };

  const handleFullScreen = (_event: React.MouseEvent) => {
    setFullScreen(!fullScreen);
  };

  const handleCloseSnackbar = () => {
    setMessageToClipboardClicked(false);
  };

  const toggleDetails = (state: boolean, message?: DeadMessage) => {
    setMessageToDetail(message);
    setShowMessageDetails(state);
  };

  const toggleRequeueResponseDetails = (state: boolean) => {
    setShowRequeueResponse(state);
    setCheckedCount(0);
    fetchQueueMessages(selectedQueues);
    if (state) {
      fetchSelectedCount();
      onRefreshByChangingQueue();
    }
  };

  const moveMessagesToOriginalQueue = useCallback(({ listOfQueues, itemsMessages }: MoveMessagesAction) => {
    if (environment && listOfQueues.length && proxyUrl) {
      const service = new OverseerService(proxyUrl, fetchApi, environment);

      return Promise.all(listOfQueues.map(queue => service.moveMessages(queue, itemsMessages, [], [])))
        .then((result) => result.reduce((acc, cur) => [...acc, ...cur], [] as MovedMessageInfo[]))
        .then(r => setMovedMessages(r));
    }
    return Promise.resolve([] as MovedMessageInfo[]);
  }, [proxyUrl, fetchApi, environment]);


  const handleRequeueRequest = () => {
    moveMessagesToOriginalQueue({
      environment: environment,
      listOfQueues: selectedQueues,
      itemsMessages: checkedCount > 0 ? items.filter(m => m.isChecked) : items
    })
      .then(_ => toggleRequeueResponseDetails(true));
  }

  const scaffolderApi = useApi(scaffolderApiRef);

  const showRemoveMessagesDialog = () => {
    if (items.length === 0 || checkedCount === 0) {
      return;
    }
    setHandleRemoveRequestDialogOpen(true);
  };

  const handleRemoveRequest = async () => {
    setHandleRemoveRequestDialogOpen(false);
    if (items.length === 0 || checkedCount === 0) {
      return;
    }

    if (onGoingTask) {
      onGoingTask.unsubscribe();
    }

    const allMessagesMap = { ... await allDeadMessagesMap };

    // Send only the message_id — the scaffolder action only uses it to build the remove request.
    // Sending full payloads causes 413 PayloadTooLarge when messages are large.
    const messagesToRemove: Record<string, Array<{ properties: { message_id: string } }>> = {};
    for (const [queue, messages] of Object.entries(allMessagesMap)) {
      messagesToRemove[queue] = messages
        .filter(m => m.isChecked)
        .map(m => ({ properties: { message_id: m.properties.message_id } }));
    }

    const response = await scaffolderApi.scaffold({
      templateRef: 'remove-messages-task',
      values: {
        systemEntityRef: stringifyEntityRef(system),
        messagesToRemove,
        overseerEnvironment: environment
      }
    });

    const subs = scaffolderApi.streamLogs(response)
      .subscribe((e: any) => {
        // Completed with no error
        if (e.type === 'completion' && e.body.error === undefined) {
          subs.unsubscribe();
          const messagesMoved: MovedMessageInfo[] = e.body.output.json;
          setMovedMessages(messagesMoved);
          toggleRequeueResponseDetails(true);
        }
        // Cancelled or with error
        if (e.type === 'cancelled' || e.body.error !== undefined) {
          subs.unsubscribe();
          window.open(`/create/tasks/${response.taskId}`, '_blank');
        }
      });
    setOnGoingTask(subs);
  };

  useEffect(() => () => {
    if (onGoingTask) {
      onGoingTask.unsubscribe();
    }
  });
  return (<>
    <Card className={fullScreen ? classes.fullscreen : ''}>

      <ListItem className={classes.header}>

        <ListItemText>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5">{environment.toUpperCase()}</Typography>
            {queues.length > 0 && (
              <Chip
                label={`Total: ${selectedCount ?? '…'}`}
                size="small"
                className={classes.countChip}
                color={selectedCount !== undefined && selectedCount > 0 ? 'secondary' : 'default'}
              />
            )}
            {queues.length > 0 && selectedQueues.length < queues.length && (
              <Chip
                label={`${selectedQueues.length}/${queues.length} queues`}
                size="small"
                className={classes.countChip}
                variant="outlined"
              />
            )}
          </div>
        </ListItemText>

        {queues.length > 0 && (
          <ListItemIcon className={classes.headerItem}>
            <FormControl variant="outlined" size="small" className={classes.queueFilterControl}>
              <InputLabel id={`queue-filter-label-${environment}`}>Filter queues</InputLabel>
              <Select
                labelId={`queue-filter-label-${environment}`}
                multiple
                value={selectedQueues}
                onChange={handleQueueFilterChange}
                label="Filter queues"
                MenuProps={{
                  getContentAnchorEl: null,
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                }}
                renderValue={(selected) => {
                  const sel = selected as string[];
                  if (sel.length === queues.length) return 'All queues';
                  if (sel.length === 1) return sel[0];
                  return `${sel.length} queues selected`;
                }}
              >
                <MenuItem value="__all__">
                  <Checkbox checked={selectedQueues.length === queues.length} indeterminate={selectedQueues.length > 0 && selectedQueues.length < queues.length} size="small" />
                  <ListItemText primary="All queues" primaryTypographyProps={{ variant: 'body2' }} />
                </MenuItem>
                <Divider />
                {queues.map(q => (
                  <MenuItem key={q} value={q}>
                    <Checkbox checked={selectedQueues.includes(q)} size="small" />
                    <ListItemText primary={q} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </ListItemIcon>
        )}

        <ListItemIcon className={classes.headerItem}>
          <Button variant="contained" color="primary" disabled={loading || checkedCount === 0 || deniedRunRemoveMessage} onClick={showRemoveMessagesDialog} aria-label="confirm">
            {`Remove (${checkedCount}) selected messages`}
          </Button>
        </ListItemIcon>

        <ListItemIcon className={classes.headerItem}>
          <Button variant="contained" color="secondary" disabled={loading || !items.length} onClick={handleRequeueRequest} aria-label="confirm">
            {checkedCount > 0 ? `Re-queue (${checkedCount}) selected messages ` : `Re-queue all messages`}
          </Button>
        </ListItemIcon>

        <ListItemIcon>
          <Button onClick={handleFullScreen} children={<AspectRatioIcon />} />
        </ListItemIcon>

      </ListItem>

      <Divider className={classes.divider} />

      <CardContent className={fullScreen ? classes.fullscreenContent : ''}>

        {loading && (
          <List dense>
            <ListItem>
              <ListItemIcon>
                <UpdateIcon />
              </ListItemIcon>
              <ListItemText primary="Please wait. Checking if we have stuck messages in this environment." />
            </ListItem>
          </List>
        )}

        {!loading && !items.length && !selectedQueues.length && (
          <List dense>
            <ListItem>
              <ListItemIcon>
                <FilterListIcon />
              </ListItemIcon>
              <ListItemText primary="No queue selected. Use the filter above to select one or more queues." />
            </ListItem>
          </List>
        )}

        {!loading && !items.length && selectedQueues.length > 0 && (
          <List dense>
            <ListItem>
              <ListItemIcon>
                <LocalLaundryServiceIcon />
              </ListItemIcon>
              <ListItemText primary="There's no stuck messages to show! =D" />
            </ListItem>
          </List>
        )}

        {!loading && items.length > 0 && (<>

          <List dense>
            {items.map((msg, index) => (
              <MessageListItem key={index}
                               message={msg}
                               onSelectionChanged={onSelectionChanged}
                               onClipboardClicked={() => setMessageToClipboardClicked(true)}
                               onDetailsClicked={() => toggleDetails(true, msg)} />
            ))}
          </List>

          {items.length >= 100 && (
            <List dense>
              <ListItem className={classes.loadMore} >
                <Button variant="outlined" color="secondary" fullWidth onClick={fetchMoreIfNeeded}>
                  Load more
                </Button>
              </ListItem>
            </List>
          )}
        </>)}
      </CardContent>
    </Card>
    <Snackbar
      open={messageToClipboardClicked}
      autoHideDuration={3000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleCloseSnackbar} severity="info" variant="filled">
        Copied To Clipboard!
      </Alert>
    </Snackbar>
    {messageToDetail && (
      <Drawer
        anchor="right"
        open={showMessageDetails}
        onClose={() => toggleDetails(false)}
        className={classes.drawer}
        classes={{ paper: classes.drawerPaper }}>
        <MessageDetails message={messageToDetail} onCloseClicked={() => toggleDetails(false)} />
      </Drawer>
    )}
    <Dialog
      fullWidth
      maxWidth='lg'
      open={showRequeueResponse}
      onClose={() => toggleRequeueResponseDetails(false)}
    >
      <DialogTitle>Processed messages report</DialogTitle>
      <DialogContent>
        <List dense>
          {movedMessages.length > 0 && (<>
            {movedMessages.map((report, rindex) => (
              <MessageRequeueResponseListItem key={rindex} report={report} />
            ))}
          </>)}
          {!movedMessages.length && (
            <ListItem>
              <ListItemIcon>
                <ErrorOutlineIcon />
              </ListItemIcon>
              <ListItemText primary="A problem may be occurred because there's no messages requeue report to show! =(" />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => toggleRequeueResponseDetails(false)} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <SimpleConfirmDialog title='Remove messages from parking lot' text={`Are you sure you want to remove ${checkedCount} messages from the parking lot? This action cannot be undone.`}
                         open={handleRemoveRequestDialogOpen}
                         onCancel={() => { setHandleRemoveRequestDialogOpen(false); }}
                         onConfirm={handleRemoveRequest} />
  </>);
}